import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import featureFlags, {FeatureFlagName} from '../../../utils/featureFlags';
import {
  TourSection,
  TOUR_SECTIONS,
  isTourSectionSeen,
  markTourSectionSeen,
  resetAllTourSections,
} from '../utils/tourStorage';

interface TourProviderProps {
  children: ReactNode;
}

type SeenMap = Record<TourSection, boolean>;

type TourContextType = {
  seenBySection: SeenMap;
  isSectionSeen: (section: TourSection) => boolean;
  markSectionSeen: (section: TourSection) => void;
  resetAllSections: () => void;
};

const buildInitialMap = (defaultValue: boolean): SeenMap =>
  TOUR_SECTIONS.reduce((acc, section) => {
    acc[section] = defaultValue;
    return acc;
  }, {} as SeenMap);

const TourContext = createContext<TourContextType>({
  seenBySection: buildInitialMap(false),
  isSectionSeen: () => false,
  markSectionSeen: () => {
    throw new Error('markSectionSeen must be used within a TourProvider');
  },
  resetAllSections: () => {
    throw new Error('resetAllSections must be used within a TourProvider');
  },
});

export const TourProvider: React.FC<TourProviderProps> = ({children}) => {
  const [seenBySection, setSeenBySection] = useState<SeenMap>(() =>
    buildInitialMap(false),
  );

  useEffect(() => {
    const featureEnabled = featureFlags.get(FeatureFlagName.Tour);
    const next = buildInitialMap(false);
    TOUR_SECTIONS.forEach(section => {
      next[section] = !featureEnabled || isTourSectionSeen(section);
    });
    setSeenBySection(next);
  }, []);

  const markSectionSeen = useCallback((section: TourSection) => {
    markTourSectionSeen(section);
    setSeenBySection(prev => ({...prev, [section]: true}));
  }, []);

  const isSectionSeen = useCallback(
    (section: TourSection) => seenBySection[section] === true,
    [seenBySection],
  );

  const resetAllSections = useCallback(() => {
    resetAllTourSections();
    setSeenBySection(buildInitialMap(false));
  }, []);

  return (
    <TourContext.Provider
      value={{seenBySection, isSectionSeen, markSectionSeen, resetAllSections}}>
      {children}
    </TourContext.Provider>
  );
};

export const useTour = () => useContext(TourContext);
